import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BranchingQueryDefinition, edgeTypeMatrix, MobileDevice } from '@iotv/datamodel';
import { AdjacentType, MobilePush, OperatingSystem } from '@iotv/iotv-v3-types';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useContext, useEffect, useState } from 'react';
import config from '../../../config';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { MobileDeviceDAO } from '../../../data/SystemManagementDAO/mobileDeviceFns';
import { isAdminRole } from '../../../helpers/access';
import { DataGridRadioColumn } from '../../../util/DataGrid/DataGridHelpers';
import { ConfirmButton } from '../../common/ConfirmButton';
import { SingleEntityAutoQuery } from '../../generic/EntityAutoQuery';
// device environment
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';
import MobileOffIcon from '@mui/icons-material/MobileOff';
// device notifications
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import NotificationsIcon from '@mui/icons-material/Notifications';
// token
import KeyIcon from '@mui/icons-material/Key';
// endpoint
import CloudIcon from '@mui/icons-material/Cloud';
import CloudOffIcon from '@mui/icons-material/CloudOff';
const debug = process.env.REACT_APP_DEBUG && false;
const dynamo = config.aws.dynamoDB;
const compare = (a, b) => a.mobilePushPlatform.localeCompare(b.mobilePushPlatform);
const MobileDeviceCard = ({ mobileDevice, updateMobileDevice, userSelectedGroup }) => {
    const [removingDevice, setRemovingDevice] = useState(false);
    const [removingEndpoint, setRemovingEndpoint] = useState(false);
    const removeDevice = async () => {
        setRemovingDevice(true);
        const res = await MobileDeviceDAO.removeMobileDevice({ mobileDevice });
        debug && console.log('MobileDeviceCard removeDevice', res);
        if (res.err || res.data == null)
            return setRemovingDevice(false);
        if (res.data) {
            await updateMobileDevice();
            setRemovingDevice(false);
        }
    };
    const removeEndpoint = async () => {
        if (mobileDevice.platformEndpointArn === null)
            return;
        setRemovingEndpoint(true);
        const res = await MobileDeviceDAO.removeSNSEndpoint({ endpointARN: mobileDevice.platformEndpointArn });
        debug && console.log('MobileDeviceCard removeEndpoint', res);
        if (res.err || res.data == null)
            return setRemovingEndpoint(false);
        if (res.data) {
            const update = new MobileDevice(mobileDevice);
            update.platformEndpointArn = null;
            await ConsumerDao.save(update);
            await updateMobileDevice(update);
            setRemovingEndpoint(false);
        }
    };
    return (_jsx(Card, { children: _jsx(CardContent, { children: _jsxs(Stack, { gap: 1, children: [_jsxs(Stack, { direction: "row", gap: 1, children: [mobileDevice.operatingSystem === OperatingSystem.Android && _jsx(AndroidIcon, { sx: { height: 'unset', color: '#3DDC84' } }), mobileDevice.operatingSystem === OperatingSystem.iOS && _jsx(AppleIcon, { sx: { height: 'unset' } }), _jsxs(Typography, { variant: "h6", children: [mobileDevice.brand, " ", mobileDevice.model ?? mobileDevice.name] })] }), mobileDevice.mobileType && (_jsxs(Stack, { direction: "row", gap: 1, children: [mobileDevice.isProduction ? (_jsx(MobileFriendlyIcon, { color: "success", sx: { height: 'unset' } })) : (_jsx(DeveloperModeIcon, { color: "info", sx: { height: 'unset' } })), _jsx(Typography, { children: "Type:" }), _jsx(Typography, { children: mobileDevice.mobileType })] })), _jsxs(Stack, { direction: "row", gap: 1, children: [_jsx(NotificationsIcon, { sx: { color: 'gold' } }), _jsx(Typography, { children: "Platform:" }), _jsx(Typography, { children: mobileDevice.mobilePushPlatform })] }), _jsxs(Stack, { direction: "row", gap: 1, children: [_jsx(KeyIcon, { sx: { height: 'unset' } }), _jsx(Typography, { children: "Token:" }), _jsx(Typography, { sx: {
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    wordBreak: 'break-all',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                }, children: mobileDevice.deviceToken })] }), _jsxs(Stack, { direction: "row", gap: 1, children: [_jsx(CloudIcon, { sx: { height: 'unset' } }), _jsx(Typography, { children: "Endpoint:" }), _jsx(Typography, { sx: {
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    wordBreak: 'break-all',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                }, children: mobileDevice.platformEndpointArn })] }), isAdminRole(userSelectedGroup) && (_jsxs(Stack, { direction: "row", gap: 1, children: [_jsx(ConfirmButton, { variant: "outlined", confirmMessage: "Remove Device?", onConfirm: removeDevice, loading: removingDevice, disabled: removingEndpoint, startIcon: _jsx(MobileOffIcon, {}), children: "Remove Device" }), _jsx(ConfirmButton, { variant: "outlined", confirmMessage: "Remove Endpoint?", onConfirm: removeEndpoint, loading: removingEndpoint, disabled: removingDevice || mobileDevice.platformEndpointArn === null, startIcon: _jsx(CloudOffIcon, {}), children: "Remove Endpoint" })] }))] }) }) }));
};
const columnLayout = {
    display: 'flex',
    resizable: false,
    sortable: false,
    disableColumnMenu: true,
};
const mobileDeviceColumns = [
    {
        field: 'mobilePushPlatform',
        headerName: 'Platform',
        ...columnLayout,
        width: 80,
        renderCell(params) {
            let PlatformIcon = undefined;
            switch (params.value) {
                case MobilePush.FCM:
                    PlatformIcon = _jsx(AndroidIcon, { sx: { color: '#3DDC84' } });
                    break;
                case MobilePush.APNS:
                case MobilePush.APNS_SANDBOX:
                    PlatformIcon = _jsx(AppleIcon, {});
                    break;
            }
            return _jsx("div", { style: { flex: 1, display: 'flex', justifyContent: 'center' }, children: PlatformIcon });
        },
    },
    {
        field: 'name',
        headerName: 'Name',
        ...columnLayout,
        minWidth: 250,
        flex: 1,
        renderCell(params) {
            const brand = params.row.brand;
            const model = params.row.model;
            const name = brand && model ? `${brand} ${model}` : (params.formattedValue ?? params.value);
            return _jsx("div", { style: { overflow: 'hidden', textOverflow: 'ellipsis' }, children: name });
        },
    },
];
const columns = [DataGridRadioColumn, ...mobileDeviceColumns];
export const MobileDevicesTab = ({ contextCustomer, user, userSelectedGroup, userFunctions, transactionFunctions }) => {
    const isAdmin = isAdminRole(userSelectedGroup);
    const [{ isSmallView }] = useContext(AppMainViewContext);
    const [querying, setQuerying] = useState(false);
    const [selectedUser, setSelectedUser] = useState(user ?? null);
    const [mobileDevices, setMobileDevices] = useState([]);
    // data grid selection
    const [selectedMobileDevice, setSelectedMobileDevice] = useState(undefined);
    const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
    useEffect(() => {
        const selectedIds = 'ids' in rowSelectionModel ? Array.from(rowSelectionModel.ids) : rowSelectionModel;
        setSelectedMobileDevice(mobileDevices.find((md) => md.id === selectedIds[0]));
    }, [rowSelectionModel, mobileDevices]);
    // data grid selection
    const fetchAll = async () => {
        if (contextCustomer === undefined)
            return;
        setQuerying(true);
        const LastEvaluatedKey = undefined;
        const { err, data } = await ConsumerDao.baseQuery({
            TableName: dynamo.tableName,
            IndexName: dynamo.typesiName,
            ExpressionAttributeNames: {
                '#type': dynamo.typesiPrimaryKey,
                '#pk': dynamo.pKey,
                '#sk': dynamo.sKey,
            },
            ExpressionAttributeValues: {
                ':type': 'MobileDevice',
                ':pkv': 'MobileDevice:',
            },
            KeyConditionExpression: `#type = :type`,
            FilterExpression: 'begins_with(#pk, :pkv) and begins_with(#sk, :pkv)',
            Limit: 50,
            ExclusiveStartKey: LastEvaluatedKey,
        });
        setQuerying(false);
        if (err)
            return;
        if (data?.Items)
            setMobileDevices(data.Items.map((item) => new MobileDevice(item)).toSorted(compare));
    };
    const fetchAdjacent = async () => {
        if (selectedUser === null)
            return;
        if (user === undefined)
            return;
        setQuerying(true);
        const contextObject = isAdmin ? selectedUser : user;
        const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [contextObject],
            query: new BranchingQueryDefinition({
                id: contextObject.sk,
                path: [
                    { objectTypeId: 'MobileDevice', adjacencyType: AdjacentType.CHILD, edgeTypeId: edgeTypeMatrix.User.MobileDevice.user_has_mobile_device.edgeTypeId },
                ],
            }),
        });
        setQuerying(false);
        if (err)
            return;
        if (data)
            setMobileDevices(data
                .getNodesOfType('MobileDevice')
                .map((item) => new MobileDevice(item))
                .toSorted(compare));
    };
    useEffect(() => {
        if (contextCustomer !== undefined) {
            if (selectedUser === null)
                fetchAll();
            else
                fetchAdjacent();
        }
    }, [contextCustomer?.sk, isAdmin, selectedUser?.sk, user?.sk]);
    const updateMobileDevice = async (mobileDevice) => {
        const index = mobileDevices.findIndex((m) => m.id === selectedMobileDevice?.id);
        if (index !== -1) {
            setMobileDevices((m) => {
                if (mobileDevice)
                    return m.toSpliced(index, 1, mobileDevice);
                else
                    return m.toSpliced(index, 1);
            });
            setSelectedMobileDevice(mobileDevice);
        }
    };
    return (_jsxs(Stack, { minHeight: '40cqh', marginBlockStart: isAdmin ? (selectedUser !== null ? 1 : 0) : 0, gap: 1, sx: { transition: 'margin-block-start 0.15s ease-in-out' }, children: [isAdmin && (_jsxs(Stack, { direction: isSmallView ? 'column' : 'row', alignItems: 'center', gap: 1, children: [_jsx(Typography, { children: "View devices for" }), _jsx(SingleEntityAutoQuery, { contextObject: contextCustomer, entityType: "User", currentEntity: selectedUser, setCurrentEntity: (u) => setSelectedUser(u), label: "User", fullWidth: false, clearable: true })] })), _jsx(DataGrid, { loading: querying, rows: querying ? [] : mobileDevices, columns: columns, checkboxSelection: true, rowSelectionModel: rowSelectionModel, onRowSelectionModelChange: setRowSelectionModel, hideFooterSelectedRowCount: true, disableMultipleRowSelection: true, pageSizeOptions: [10], hideFooter: mobileDevices.length <= 10, hideFooterPagination: mobileDevices.length <= 10, initialState: { pagination: { paginationModel: { pageSize: 10, page: 0 } } } }), selectedMobileDevice && (_jsx(MobileDeviceCard, { mobileDevice: selectedMobileDevice, updateMobileDevice: updateMobileDevice, userSelectedGroup: userSelectedGroup }, selectedMobileDevice.sk))] }, 'MobileDevicesTabMainGrid'));
};
